import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import LogContainer from 'config-migration/shared-ui-components/LogContainer';
import {PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT, PrimaryObjectType} from 'config-migration/types/ObjectType';

import Log, {LogMessageData} from './Log';

export type LogMessageGroup = Array<LogMessageData>;

export interface LogSectionProps {
  logMessageGroups: Array<LogMessageGroup>;
  sectionDisplayName?: string;
  backgroundColor?: string;
}

const TEMPLATE_SECTION_GROUP: Array<PrimaryObjectType> = [
  PrimaryObjectType.TemplateCollection,
  PrimaryObjectType.Template,
  PrimaryObjectType.Subtemplate,
];

const WORKFLOW_SECTION_GROUP: Array<PrimaryObjectType> = [
  PrimaryObjectType.ProcessTaskSchema,
  PrimaryObjectType.ProcessOutputSchema,
  PrimaryObjectType.ProcessFlowchartConfigVersion,
];

/**
 * Returns the name of the log section for an object type. Note that the log section name can be different
 * from the import plan section name.
 */
export function getSectionNameForObjectType(objectType: PrimaryObjectType): string {
  if (TEMPLATE_SECTION_GROUP.includes(objectType)) {
    return 'Templates';
  }
  if (WORKFLOW_SECTION_GROUP.includes(objectType)) {
    return pluralize(2, PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[PrimaryObjectType.ProcessTaskSchema]);
  }
  return pluralize(2, PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[objectType]);
}

/**
 * Component that renders "groups" of Logs associated with a particular section of the migration. Examples of
 * sections include schema object types like "Result Schema", or a group of object types like "Templates".
 * Each section is rendered with a title, and each group of Logs is separated by a line break.
 */
export default function LogSection(props: LogSectionProps): JSX.Element {
  return (
    <LogContainer backgroundColor={props.backgroundColor} header={props.sectionDisplayName}>
      {props.logMessageGroups.map((group, i) => (
        <div
          key={i}
          className={classNames(i !== props.logMessageGroups.length - 1 ? css(styles.logGroup) : null)}
        >
          <Log logMessages={group} />
        </div>
      ))}
    </LogContainer>
  );
}

const styles = StyleSheet.create({
  logGroup: {
    marginBottom: 18,
  },
});
